import { useState, useEffect, useRef } from "react";
import { useAuth }    from "@context/AuthContext";
import { useRole }    from "@hooks/useRole";
import AddCandidateModal from "@features/candidates/AddCandidateModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import cache          from "@utils/cache";
import axios          from "axios";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";
import { Modal, ModalFooter } from "@common/Modal";


const COLORS = ["var(--fc-primary)","var(--fc-green)","var(--fc-amber)","#EC4899","#06B6D4","#7C3AED","var(--fc-red)","#F97316","#0EA5E9","#15803D"];
const PER_PAGE = 10;

const post = (ep, fields) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k,v]) => fd.append(k, v));
  return axios.post(`${API_BASE}${ep}`, fd).then(r => r.data).catch(() => null);
};

// ── Availability ring (clickable) ─────────────────────────────────────────────
function AvailRing({ value, onClick }) {
  const v = parseInt(value) || 0;
  const r = 16, circ = 2 * Math.PI * r;
  const dash  = Math.min(v / 40, 1) * circ;
  const color = v >= 30 ? "var(--fc-green)" : v >= 10 ? "var(--fc-amber)" : "var(--fc-border)";
  return (
    <div onClick={onClick} title="Click to edit availability"
      style={{ position:"relative", width:40, height:40, cursor:"pointer", flexShrink:0 }}>
      <svg width="40" height="40" style={{ transform:"rotate(-90deg)" }}>
        <circle cx="20" cy="20" r={r} fill="none" style={{ stroke:"var(--fc-border-light)" }} strokeWidth="3.5"/>
        <circle cx="20" cy="20" r={r} fill="none" stroke={color} strokeWidth="3.5"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"/>
      </svg>
      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:"var(--fc-text-primary)" }}>{v}</div>
    </div>
  );
}

// ── Availability Modal ────────────────────────────────────────────────────────
function AvailabilityModal({ item, userSlug, onClose, onUpdate }) {
  const [val,          setVal]          = useState(parseInt(item.availability) || 0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error,        setError]        = useState("");

  const availColor = val >= 30 ? "var(--fc-green)" : val >= 10 ? "var(--fc-amber)" : "var(--fc-red)";
  const availLabel = val >= 30 ? "Available" : val >= 10 ? "Partially Available" : "Busy";

  const handleSave = async () => {
    setIsSubmitting(true); setError("");
    const res = await post("save-resource-availability", {
      current_user_slug: userSlug,
      resource_slug:     item.user_slug,
      availability:      val,
    });
    if (res && Number(res.status) === 1) {
      onUpdate(item.user_slug, val);
      onClose();
    } else {
      setError(res?.message || "Something went wrong.");
    }
    setIsSubmitting(false);
  };

  const title = (
    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ width:36, height:36, borderRadius:10, background:"var(--fc-primary-light)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <i className="fa-solid fa-clock" style={{ color:"var(--fc-primary)", fontSize:14 }} />
      </div>
      <div>
        <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)" }}>Update Availability</div>
        <div style={{ fontSize:12, color:"var(--fc-text-muted)" }}>{item.resource_name}</div>
      </div>
    </div>
  );

  return (
    <Modal title={title} onClose={onClose} maxWidth={400}
      footer={
        <ModalFooter onCancel={onClose} onConfirm={handleSave} confirmBusy={isSubmitting}
          confirmLabel={isSubmitting ? "Saving..." : "Save"} />
      }>
      <div style={{ textAlign:"center", marginBottom:24 }}>
        <div style={{ fontSize:56, fontWeight:900, color:availColor, lineHeight:1 }}>{val}</div>
        <div style={{ fontSize:14, fontWeight:600, color:availColor, marginTop:6 }}>hrs/week — {availLabel}</div>
      </div>

      <input type="range" min={0} max={40} value={val}
        onChange={e => setVal(parseInt(e.target.value))}
        style={{ width:"100%", accentColor:availColor }} />
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"var(--fc-text-subtle)", marginTop:4 }}>
        <span>0 — Busy</span><span>20 hrs</span><span>40 — Available</span>
      </div>

      <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:18, flexWrap:"wrap" }}>
        {[0, 10, 20, 30, 40].map(v => (
          <button key={v} onClick={() => setVal(v)}
            style={{ padding:"5px 14px", borderRadius:99, border:`1.5px solid ${val===v ? availColor : "var(--fc-border)"}`, background:val===v ? availColor+"18" : "var(--fc-card-bg)", color:val===v ? availColor : "var(--fc-text-muted)", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}>
            {v} hrs
          </button>
        ))}
      </div>

      {error && <div style={{ marginTop:12, padding:"8px 12px", background:"var(--fc-red-light)", border:"1px solid var(--fc-red-border)", borderRadius:8, fontSize:12, color:"var(--fc-red)" }}>{error}</div>}
    </Modal>
  );
}

// ── Toggle switch ─────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, loading }) {
  return (
    <div onClick={!loading ? onChange : undefined}
      style={{ width:38, height:21, borderRadius:99, background:checked?"var(--fc-primary)":"var(--fc-border)", cursor:loading?"wait":"pointer", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
      <div style={{ position:"absolute", top:3, left:checked?20:3, width:15, height:15, borderRadius:"50%", background:"#fff", transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.15)" }} />
      {loading && <div style={{ position:"absolute", inset:0, borderRadius:99, background:"rgba(255,255,255,0.5)", display:"flex", alignItems:"center", justifyContent:"center" }}><i className="fa-solid fa-spinner fa-spin" style={{fontSize:8,color:"var(--fc-text-muted)"}} /></div>}
    </div>
  );
}

// ── Confirm dialog ────────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <Modal title="Are you sure?" maxWidth={360}
      footer={<ModalFooter onCancel={onCancel} onConfirm={onConfirm} confirmLabel="Yes, Update" />}>
      <p style={{ fontSize:13, color:"var(--fc-text-muted)", textAlign:"center" }}>{message}</p>
    </Modal>
  );
}

// ── Skeleton row ──────────────────────────────────────────────────────────────
function SkeletonRow() {
  const col = (w) => (
    <td style={{ padding:"13px 14px" }}>
      <div style={{ height:13, background:"var(--fc-border-light)", borderRadius:6, width:w }} />
    </td>
  );
  return (
    <tr style={{ borderBottom:"1px solid var(--fc-border-light)" }}>
      <td style={{ padding:"13px 14px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:38, height:38, borderRadius:9, background:"var(--fc-border-light)", flexShrink:0 }} />
          <div>
            <div style={{ height:13, background:"var(--fc-border-light)", borderRadius:6, width:140, marginBottom:6 }} />
            <div style={{ height:10, background:"var(--fc-bg-light)", borderRadius:5, width:110 }} />
          </div>
        </div>
      </td>
      {col(40)}{col(40)}{col(120)}{col(40)}
      <td style={{ padding:"13px 14px" }}>
        <div style={{ display:"flex", gap:6 }}>
          <div style={{ width:85, height:28, background:"var(--fc-border-light)", borderRadius:7 }} />
          <div style={{ width:50, height:28, background:"var(--fc-border-light)", borderRadius:7 }} />
        </div>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ResourcesPage() {
  const { user, userSlug } = useAuth();
  const navigate = useNavigate();

  const { isFirmOwner, isRM, firmType, isStaffingFirm } = useRole();
  const firmId            = user?.firm_details?.firm_id || user?.firm_id;
  const user_id           = user?.user_id;
  const canManage = isFirmOwner || isRM;

  const [resources,   setResources]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filterText,  setFilterText]  = useState(""); // debounced
  const [searchInput, setSearchInput] = useState("");       // raw input
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);
  const [showModal,   setShowModal]   = useState(false);
  const [searchParams] = useSearchParams();

  // Arriving from the dashboard's "Add Resource" quick action goes straight
  // into the add-candidate flow instead of just landing on the list
  useEffect(() => {
    if (searchParams.get("add") === "1") setShowModal(true);
  }, [searchParams]);

  const [availModal,  setAvailModal]  = useState(null);
  const [confirmSelf, setConfirmSelf] = useState(null); // { item, index }
  const [confirmAdv,  setConfirmAdv]  = useState(null); // { item, index }
  const [toggling,    setToggling]    = useState({});
  const [toast,       setToast]       = useState(null);
  const searchTimer = useRef();

  const CACHE_KEY = `resources_${userSlug}`;
  const pageTitle = isStaffingFirm ? "My Team" : "My Candidates";
  const addLabel  = isStaffingFirm ? "Add Team Member" : "Add Candidate";
  const detailPath = (slug) => isStaffingFirm ? `/my-team/details/${slug}` : `/resources/details/${slug}`;
  const editPath   = (slug) => isStaffingFirm ? `/my-team/edit-resource/${slug}` : `/resources/edit-resource/${slug}`;

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(() => setToast(null), 3000); };

  // ── checkSelfServices — matches old logic exactly ─────────────────────────
  const checkSelfServices = (firmIds) => {
    if (firmIds !== null && firmIds !== undefined && firmIds !== "") {
      return firmIds.split(", ").indexOf(String(firmId)) !== -1;
    }
    return false;
  };

  // ── Data fetching ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userSlug) return;
    let ignore = false;

    // Cache hit — show immediately, no API call
    if (page === 1 && !filterText) {
      const cached = cache.get(CACHE_KEY);
      if (cached) {
        setResources(cached.list   || []);
        setTotalPages(cached.totalPages || 1);
        setTotalCount(cached.totalCount || 0);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    const fd = new FormData();
    fd.append("user_slug",   userSlug);
    fd.append("filter_text", filterText);
    fd.append("limit",       PER_PAGE);
    fd.append("offset",      (page - 1) * PER_PAGE);

    axios.post(`${API_BASE}get-my-resource-listing`, fd)
      .then(r => {
        if (ignore) return;
        const data  = r.data;
        const list  = Array.isArray(data?.resourceList) ? data.resourceList : [];
        const pages = data?.pagination?.total_pages != null
          ? data.pagination.total_pages
          : (Math.ceil((data?.pagination?.total || list.length) / PER_PAGE) || 1);
        const total = data?.pagination?.total != null ? data.pagination.total : list.length;
        if (page === 1 && !filterText) {
          cache.set(CACHE_KEY, { list, totalPages: pages, totalCount: total });
        }
        setResources(list);
        setTotalPages(pages);
        setTotalCount(total);
        setLoading(false);
      })
      .catch(() => { if (!ignore) setLoading(false); });

    return () => { ignore = true; };
  }, [userSlug, page, filterText]);

  // Debounce search input → filterText (resets page to 1)
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (filterText !== searchInput) {
        cache.clear(CACHE_KEY);
        setPage(1);
        setFilterText(searchInput);
      }
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchInput]);

  // ── updateAdvertised — confirm first, then API, sends current value, server flips ──
  const processAdvertised = async (item, index) => {
    setToggling(t => ({...t, [item.user_slug]:"adv"}));
    setConfirmAdv(null);
    const res = await post("update-is-advertised", {
      user_slug:     userSlug,
      resource_slug: item.user_slug,
      is_advertised: parseInt(item.is_advertised), // send current value as int (server flips it)
    });
    if (res && Number(res.status) === 1) {
      setResources(rs => {
        const updated = [...rs];
        updated[index] = { ...updated[index], is_advertised: item.is_advertised === "0" ? "1" : "0" };
        const cached = cache.get(CACHE_KEY);
        if (cached) cache.set(CACHE_KEY, { ...cached, list: updated });
        return updated;
      });
      showToast(item.is_advertised === "0" ? "Now visible to clients" : "Hidden from clients");
    } else {
      showToast(res?.message || "Failed to update visibility", "error");
    }
    setToggling(t => { const n={...t}; delete n[item.user_slug]; return n; });
  };

  // ── updateSelfService — confirm first, then API, replace full item ─────────
  const processSelfService = async (item, index) => {
    setToggling(t => ({...t, [item.user_slug]:"self"}));
    setConfirmSelf(null);
    const res = await post("update-self-service", {
      user_slug:     userSlug,
      resource_slug: item.user_slug,
    });
    if (res && Number(res.status) === 1) {
      // Replace full item with server response (firm_ids_self_services changes)
      if (res.resource) {
        setResources(rs => {
          const updated = [...rs];
          updated[index] = res.resource;
          const cached = cache.get(CACHE_KEY);
          if (cached) cache.set(CACHE_KEY, { ...cached, list: updated });
          return updated;
        });
      } else {
        // Fallback: reload from server
        cache.clear(CACHE_KEY);
        loadResources(filterText, page);
      }
      showToast("Self-service access updated!");
    } else {
      showToast(res?.message || "Failed to update self-service", "error");
    }
    setToggling(t => { const n={...t}; delete n[item.user_slug]; return n; });
  };

  // ── handleAvailabilityUpdate ──────────────────────────────────────────────
  const handleAvailabilityUpdate = (slug, newValue) => {
    setResources(rs => {
      const updated = rs.map(r => r.user_slug === slug ? {...r, availability: String(newValue)} : r);
      cache.set(CACHE_KEY, updated);
      return updated;
    });
    showToast("Availability updated!");
  };

  // ── Pagination ────────────────────────────────────────────────────────────
  // Server handles pagination — resources is already the current page

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:800, color:"var(--fc-text-primary)", margin:0 }}>{pageTitle}</h1>
          <p style={{ fontSize:13, color:"var(--fc-text-muted)", margin:"4px 0 0" }}>
            {!loading && <><strong style={{color:"var(--fc-text-primary)"}}>{totalCount}</strong> profiles · </>}
            Page {page} of {totalPages||1}
          </p>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          {/* Search */}
          <div style={{ display:"flex", alignItems:"center", gap:8, background:"var(--fc-card-bg)", border:"1.5px solid var(--fc-border)", borderRadius:10, padding:"8px 14px", minWidth:260, transition:"border-color 0.15s" }}
            onFocusCapture={e=>e.currentTarget.style.borderColor="var(--fc-primary)"}
            onBlurCapture={e=>e.currentTarget.style.borderColor="var(--fc-border)"}>
            <i className="fa-solid fa-magnifying-glass" style={{ fontSize:12, color:"var(--fc-text-subtle)", flexShrink:0 }} />
            <input value={searchInput} onChange={e=>setSearchInput(e.target.value)}
              placeholder="Search name, role, location…"
              style={{ border:"none", outline:"none", fontSize:13, color:"var(--fc-text-primary)", flex:1, fontFamily:"inherit", background:"transparent" }} />
            {searchInput && (
              <button onClick={()=>{setSearchInput("");setFilterText("");}}
                style={{ border:"none", background:"none", cursor:"pointer", color:"var(--fc-text-subtle)", padding:0 }}>
                <i className="fa-solid fa-xmark" style={{fontSize:11}} />
              </button>
            )}
          </div>
          {canManage && (
            <button onClick={()=>setShowModal(true)} className="fc-btn fc-btn-md fc-btn-primary fc-btn-pill">
              <i className="fa-solid fa-user-plus me-1" style={{fontSize:11}} />{addLabel}
            </button>
          )}
        </div>
      </div>

      {/* ── List ── */}
      {loading ? (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>
          {[...Array(6)].map((_,i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:14, padding:"16px 20px", borderBottom:i<5?"1px solid var(--fc-border-light)":"none" }}>
              <div style={{ width:42, height:42, borderRadius:11, background:"var(--fc-border-light)", flexShrink:0 }} />
              <div style={{ flex:1 }}>
                <div style={{ height:13, background:"var(--fc-border-light)", borderRadius:4, width:"40%", marginBottom:8 }} />
                <div style={{ height:10, background:"var(--fc-bg-light)", borderRadius:4, width:"25%" }} />
              </div>
              <div style={{ height:10, background:"var(--fc-bg-light)", borderRadius:4, width:100 }} />
              <div style={{ height:22, background:"var(--fc-border-light)", borderRadius:99, width:42 }} />
              <div style={{ height:22, background:"var(--fc-border-light)", borderRadius:99, width:42 }} />
              <div style={{ width:42, height:42, borderRadius:99, background:"var(--fc-border-light)" }} />
              <div style={{ display:"flex", gap:6 }}>
                <div style={{ height:30, background:"var(--fc-border-light)", borderRadius:8, width:60 }} />
                <div style={{ height:30, width:30, background:"var(--fc-border-light)", borderRadius:8 }} />
              </div>
            </div>
          ))}
        </div>
      ) : resources.length === 0 ? (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:"60px 24px", textAlign:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ width:56, height:56, borderRadius:16, background:"var(--fc-border-light)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
            <i className="fa-solid fa-users-slash" style={{ fontSize:22, color:"var(--fc-text-hint)" }} />
          </div>
          <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:6 }}>No candidates found</div>
          <div style={{ fontSize:13, color:"var(--fc-text-subtle)", marginBottom:18 }}>
            {filterText ? `No results for "${filterText}"` : "Add your first candidate to get started"}
          </div>
          {!filterText && canManage && (
            <button onClick={()=>setShowModal(true)} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
              <i className="fa-solid fa-user-plus me-1" style={{fontSize:11}} />{addLabel}
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>

            {/* Header row */}
            <div style={{ display:"grid", gridTemplateColumns:"minmax(200px,2.5fr) minmax(130px,1.4fr) 88px 110px 68px 130px", padding:"10px 20px", background:"var(--fc-bg-light)", borderBottom:"1.5px solid var(--fc-border)", gap:0 }}>
              {[["Name & Role","left"],["Location","left"],["Visible","center"],["Self-Service","center"],["Avail","center"],["Actions","right"]].map(([h,a]) => (
                <div key={h} style={{ fontSize:10, fontWeight:700, color:"var(--fc-text-subtle)", textTransform:"uppercase", letterSpacing:"0.09em", textAlign:a }}>{h}</div>
              ))}
            </div>

            {/* Rows */}
            {resources.map((item, index) => {
              const name    = item.resource_name || "—";
              const initials= name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
              const color   = COLORS[index % COLORS.length];
              const location= [item.city_name, item.state_name, item.country_name].filter(Boolean).join(", ");
              const isSelf  = checkSelfServices(item.firm_ids_self_services);
              const canEdit = isFirmOwner || (isRM && (String(user_id)===String(item.resource_manager_id)||String(item.firm_id)===String(firmId)));
              const avail   = parseInt(item.availability)||0;
              const isLast  = index === resources.length - 1;

              return (
                <div key={item.user_slug||index}
                  style={{ display:"grid", gridTemplateColumns:"minmax(200px,2.5fr) minmax(130px,1.4fr) 88px 110px 68px 130px", alignItems:"center", padding:"13px 20px", borderBottom:isLast?"none":"1px solid var(--fc-border-light)", transition:"background 0.1s", gap:0 }}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--fc-bg-light)"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>

                  {/* Name & Role */}
                  <div style={{ display:"flex", alignItems:"center", gap:12, paddingRight:16, minWidth:0 }}>
                    {/* Avatar */}
                    <div onClick={()=>navigate(detailPath(item.user_slug))}
                      style={{ width:42, height:42, borderRadius:11, background:"var(--fc-border-light)", border:"1px solid var(--fc-border)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, cursor:"pointer", overflow:"hidden" }}>
                      {item.profile_image_path
                        ? <img src={PROFILE_IMG_BASE+item.profile_image_path} alt={name}
                            style={{ width:"100%", height:"100%", objectFit:"cover" }}
                            onError={e=>{e.target.style.display="none"; if(e.target.nextSibling) e.target.nextSibling.style.display="flex";}} />
                        : null}
                      <i className="fa-solid fa-user" style={{ fontSize:16, color:"var(--fc-text-subtle)", display:item.profile_image_path?"none":"flex" }} />
                    </div>
                    <div style={{ minWidth:0, flex:1 }}>
                      <div onClick={()=>navigate(detailPath(item.user_slug))}
                        style={{ fontSize:13, fontWeight:700, color:"var(--fc-text-primary)", cursor:"pointer", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", lineHeight:1.35 }}>
                        {name}
                      </div>
                      <div style={{ fontSize:11, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginTop:2,
                        color: item.user_profile_role ? "var(--fc-primary)" : "var(--fc-text-hint)",
                        fontStyle: item.user_profile_role ? "normal" : "italic" }}>
                        {item.user_profile_role || "No role set"}
                      </div>
                    </div>
                  </div>

                  {/* Location */}
                  <div style={{ paddingRight:12, minWidth:0 }}>
                    {location ? (
                      <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:"var(--fc-text-muted)" }}>
                        <i className="fa-solid fa-location-dot" style={{ fontSize:9, color:"var(--fc-text-hint)", flexShrink:0 }} />
                        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{location}</span>
                      </div>
                    ) : <span style={{ fontSize:12, color:"var(--fc-border)" }}>—</span>}
                  </div>

                  {/* Visible */}
                  <div style={{ display:"flex", justifyContent:"center" }}>
                    {canEdit
                      ? <Toggle checked={item.is_advertised==="1"} loading={toggling[item.user_slug]==="adv"} onChange={()=>setConfirmAdv({item,index})} />
                      : <span style={{ fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:99,
                          background:item.is_advertised==="1"?"var(--fc-green-light)":"var(--fc-bg-light)",
                          color:item.is_advertised==="1"?"var(--fc-green)":"var(--fc-text-subtle)",
                          border:"1px solid "+(item.is_advertised==="1"?"var(--fc-green-border)":"var(--fc-border)") }}>
                          {item.is_advertised==="1"?"Yes":"No"}
                        </span>}
                  </div>

                  {/* Self-Service — only the specific RM who manages this
                      resource gets the actual toggle, matching the same rule
                      used on the individual profile page; everyone else
                      (including Owner) sees a read-only indicator. */}
                  <div style={{ display:"flex", justifyContent:"center" }}>
                    {String(item.resource_manager_id) === String(user?.user_id)
                      ? <Toggle checked={isSelf} loading={toggling[item.user_slug]==="self"} onChange={()=>setConfirmSelf({item,index})} />
                      : <span style={{ fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:99,
                          background:isSelf?"var(--fc-green-light)":"var(--fc-bg-light)",
                          color:isSelf?"var(--fc-green)":"var(--fc-text-subtle)",
                          border:"1px solid "+(isSelf?"var(--fc-green-border)":"var(--fc-border)") }}>
                          {isSelf?"On":"Off"}
                        </span>}
                  </div>

                  {/* Availability */}
                  <div style={{ display:"flex", justifyContent:"center" }}>
                    <AvailRing value={avail} onClick={()=>canEdit&&setAvailModal(item)} />
                  </div>

                  {/* Actions */}
                  <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
                    <button onClick={()=>navigate(detailPath(item.user_slug))}
                      style={{ height:32, padding:"0 14px", borderRadius:8, border:"1px solid var(--fc-border)", background:"var(--fc-card-bg)", color:"var(--fc-text-secondary)", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:5, transition:"all 0.15s", whiteSpace:"nowrap" }}
                      onMouseEnter={e=>{e.currentTarget.style.background="var(--fc-primary-light)";e.currentTarget.style.borderColor="var(--fc-primary-border)";e.currentTarget.style.color="var(--fc-primary)";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="var(--fc-card-bg)";e.currentTarget.style.borderColor="var(--fc-border)";e.currentTarget.style.color="var(--fc-text-secondary)";}}>
                      <i className="fa-solid fa-eye" style={{fontSize:10}} /> View
                    </button>
                    {canEdit && (
                      <button onClick={()=>navigate(editPath(item.user_slug))}
                        style={{ width:32, height:32, borderRadius:8, border:"1px solid var(--fc-border)", background:"var(--fc-card-bg)", color:"var(--fc-text-muted)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s" }}
                        onMouseEnter={e=>{e.currentTarget.style.background="var(--fc-primary)";e.currentTarget.style.color="#fff";e.currentTarget.style.borderColor="var(--fc-primary)";}}
                        onMouseLeave={e=>{e.currentTarget.style.background="var(--fc-card-bg)";e.currentTarget.style.color="var(--fc-text-muted)";e.currentTarget.style.borderColor="var(--fc-border)";}}>
                        <i className="fa-solid fa-pen" style={{fontSize:10}} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:16, flexWrap:"wrap", gap:10 }}>
              <span style={{ fontSize:13, color:"var(--fc-text-muted)" }}>
                Showing <strong style={{color:"var(--fc-text-primary)"}}>{(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE,(page-1)*PER_PAGE+resources.length)}</strong> of <strong style={{color:"var(--fc-text-primary)"}}>{totalCount}</strong>
              </span>
              <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                  style={{ width:34,height:34,borderRadius:8,border:"1.5px solid var(--fc-border)",background:"var(--fc-card-bg)",cursor:page===1?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--fc-text-muted)",opacity:page===1?0.4:1,transition:"all 0.15s" }}
                  onMouseEnter={e=>page>1&&(e.currentTarget.style.borderColor="var(--fc-primary)")}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="var(--fc-border)"}>
                  <i className="fa-solid fa-chevron-left" style={{fontSize:10}} />
                </button>
                {Array.from({length:totalPages},(_,i)=>i+1).map(p=>{
                  const show=p===1||p===totalPages||(p>=page-1&&p<=page+1);
                  const dot=(p===page-2&&page>3)||(p===page+2&&page<totalPages-2);
                  if(dot) return <span key={p} style={{color:"var(--fc-text-hint)",padding:"0 2px",fontSize:14,lineHeight:1}}>···</span>;
                  if(!show) return null;
                  return (
                    <button key={p} onClick={()=>setPage(p)}
                      style={{ width:34,height:34,borderRadius:8,border:`1.5px solid ${p===page?"var(--fc-primary)":"var(--fc-border)"}`,background:p===page?"var(--fc-primary)":"var(--fc-card-bg)",color:p===page?"#fff":"var(--fc-text-muted)",fontSize:12,fontWeight:p===page?700:400,cursor:"pointer",transition:"all 0.15s" }}>
                      {p}
                    </button>
                  );
                })}
                <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                  style={{ width:34,height:34,borderRadius:8,border:"1.5px solid var(--fc-border)",background:"var(--fc-card-bg)",cursor:page===totalPages?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--fc-text-muted)",opacity:page===totalPages?0.4:1,transition:"all 0.15s" }}
                  onMouseEnter={e=>page<totalPages&&(e.currentTarget.style.borderColor="var(--fc-primary)")}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="var(--fc-border)"}>
                  <i className="fa-solid fa-chevron-right" style={{fontSize:10}} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Modals ── */}
      {showModal && (
        <AddCandidateModal
          firmId={firmId}
          userSlug={userSlug}
          firmType={firmType}
          onClose={() => setShowModal(false)}
          onAdd={() => { cache.clear(CACHE_KEY); setPage(1); }}
        />
      )}

      {availModal && (
        <AvailabilityModal
          item={availModal}
          userSlug={userSlug}
          onClose={() => setAvailModal(null)}
          onUpdate={handleAvailabilityUpdate}
        />
      )}

      {confirmSelf && (
        <ConfirmDialog
          message={`Update self-service access for ${confirmSelf.item.resource_name}?`}
          onConfirm={() => processSelfService(confirmSelf.item, confirmSelf.index)}
          onCancel={() => setConfirmSelf(null)}
        />
      )}

      {confirmAdv && (
        <ConfirmDialog
          message={`${confirmAdv.item.is_advertised === "1" ? "Hide" : "Show"} ${confirmAdv.item.resource_name} ${confirmAdv.item.is_advertised === "1" ? "from" : "to"} clients?`}
          onConfirm={() => processAdvertised(confirmAdv.item, confirmAdv.index)}
          onCancel={() => setConfirmAdv(null)}
        />
      )}

      {toast && (
        <div style={{ position:"fixed", bottom:24, right:24, zIndex:9999,
          background:toast.type==="error"?"var(--fc-red-light)":"#111827",
          border:toast.type==="error"?"1px solid var(--fc-red-border)":"none",
          borderRadius:12, padding:"12px 18px", display:"flex", alignItems:"center", gap:10,
          boxShadow:"0 8px 32px rgba(0,0,0,0.2)", animation:"fadeIn 0.2s" }}>
          <div style={{ width:22,height:22,borderRadius:6,background:toast.type==="error"?"var(--fc-red)":"var(--fc-green)",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <i className={`fa-solid ${toast.type==="error"?"fa-xmark":"fa-check"}`} style={{fontSize:10,color:"#fff"}} />
          </div>
          <span style={{ fontSize:13,fontWeight:600,color:toast.type==="error"?"var(--fc-red)":"#fff" }}>{toast.msg}</span>
        </div>
      )}
    </div>
  );
}